<template>
  <button type="button" class="gallery-button" @click="handleClick">
    <span class="gallery-button__surface">
      <span class="gallery-button__text">{{ photoCountText }}</span>
      <FavoriteConIcon />
    </span>
  </button>
</template>

<script setup lang="ts">
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import ru from '../i18n/ru.json';
import FavoriteConIcon from '../icons/FavoriteConIcon.vue';

type Props = {
  count: number;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'click'): void;
}>();

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const photoCountText = computed(() => t('photosCount', { count: props.count }));

const handleClick = () => {
  emit('click');
};
</script>

<style scoped lang="scss">
.gallery-button {
  position: absolute;
  inset: auto var(--alias-space-16) var(--alias-space-16) auto;
  z-index: 3;
  box-sizing: border-box;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 0;
  padding: 0;
  color: var(--alias-color-text-default);
  cursor: pointer;
  background: transparent;
  border: none;
  transition: opacity 0.2s ease;

  @include mobile {
    inset: auto var(--alias-space-16) var(--alias-space-16) auto;
  }
}

.gallery-button__surface {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  height: 24px;
  padding: var(--alias-space-4) var(--alias-space-4) var(--alias-space-4) var(--alias-space-8);
  outline: 1px solid var(--alias-color-border-default);
  background-color: var(--comp-button-primary-color-background-inverted);
  border-radius: var(--alias-radius-rounded, 999px);
  box-shadow:
    0 1px 1px 0 var(--global-color-transparent-black-a050),
    0 4px 16px 0 var(--global-color-transparent-black-a100);

  &:hover {
    background-color: var(--comp-button-primary-color-background-inverted-hovered);
  }

  &:active {
    background-color: var(--comp-button-primary-color-background-inverted-pressed);
  }
}

.gallery-button__text {
  white-space: nowrap;

  @include base-200-200-medium;
}
</style>
